import { useState, useEffect } from 'react';
import { useStore } from '@/store/useStore';
import {
    ShoppingBag, ChevronLeft,
    Share2, User, FileCode,
} from 'lucide-react';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { StatusModal } from '@/components/Modals/StatusModal';
import { PurchaseSuccessModal } from '@/components/Modals/PurchaseSuccessModal';
import { useNotify } from '@/context/NotificationContext';
import { apiFetch, usernameMarketApi } from '@/utils/api';
import { useTranslation } from 'react-i18next';
import { UsernameValuationCard } from '@/components/Shared/UsernameValuationCard';

// --- HELPER COMPONENTS (Copied from CardDetailsPage) ---

const GramIcon = ({ className }: { className?: string }) => (
    <svg viewBox="12 12 56 56" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
        <path fill="#30a1f5" d="M52.017 12.097H27.984c-3.201 0-4.802 0-6.25.448a10 10 0 0 0-3.496 1.909c-1.159.975-2.024 2.322-3.755 5.014l-7.64 11.884c-1.144 1.78-1.716 2.668-1.87 3.605a4.6 4.6 0 0 0 .263 2.45c.35.882 1.098 1.63 2.593 3.125L36.217 68.92c1.325 1.324 1.987 1.986 2.75 2.234a3.34 3.34 0 0 0 2.067 0c.763-.248 1.425-.91 2.75-2.234l28.388-28.388c1.495-1.495 2.243-2.243 2.593-3.125.31-.778.4-1.625.263-2.45-.155-.937-.727-1.826-1.87-3.605l-7.64-11.884c-1.73-2.692-2.596-4.039-3.756-5.014a10 10 0 0 0-3.496-1.91c-1.448-.447-3.048-.447-6.249-.447" />
        <path fill="#fff" d="M47.465 21.472c.39-1.055 1.883-1.055 2.274 0l2.698 7.292a1.6 1.6 0 0 0 .945.946l7.293 2.698c1.055.39 1.055 1.883 0 2.274l-7.293 2.698a1.6 1.6 0 0 0-.945.945l-2.698 7.293c-.39 1.055-1.883 1.055-2.274 0l-2.698-7.293a1.6 1.6 0 0 0-.946-.945l-7.292-2.698c-1.055-.39-1.055-1.883 0-2.274l7.292-2.698a1.6 1.6 0 0 0 .946-.946z" />
    </svg>
);

const SectionHeader = ({ icon: Icon, title }: { icon: any, title: string }) => (
    <div className="flex items-center gap-2 mb-4 px-1">
        <Icon size={16} className="text-blue-500" />
        <h3 className="text-[12px] font-black uppercase tracking-[0.2em] text-white/80" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            {title}
        </h3>
    </div>
);

const GlowInfoCard = ({ label, value, subValue, icon: Icon, color = "blue" }: { label: string, value: string | number, subValue?: string, icon: any, color?: string }) => {
    const colorMap: Record<string, string> = {
        blue: "text-blue-400 bg-blue-500/10 border-blue-500/20 shadow-blue-500/5",
        green: "text-green-400 bg-green-500/10 border-green-500/20 shadow-green-500/5",
        purple: "text-purple-400 bg-purple-500/10 border-purple-500/20 shadow-purple-500/5",
        orange: "text-orange-400 bg-orange-500/10 border-orange-500/20 shadow-orange-500/5"
    };

    return (
        <div className={`p-4 rounded-[22px] border bg-tg-secondary border-white/[0.04] transition-all duration-300 hover:border-white/5 group`}>
            <div className="flex items-center gap-3 mb-2.5">
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center border ${colorMap[color] || colorMap.blue}`}>
                    <Icon size={14} />
                </div>
                <span className="text-[8px] font-black text-gray-500 uppercase tracking-widest">{label}</span>
            </div>
            <div className="flex flex-col">
                <span className="text-[13px] font-black text-white/90 group-hover:text-white transition-colors truncate">{value}</span>
                {subValue && <span className="text-[9px] font-bold text-gray-600 uppercase mt-0.5">{subValue}</span>}
            </div>
        </div>
    );
};

// --- MAIN COMPONENT ---

interface BrandItemDetailsProps {
    id: string | number | any;
    onBack: () => void;
    onNavigate?: (page: string, data?: any) => void;
}

export default function BrandItemDetails({ id, onBack, onNavigate }: BrandItemDetailsProps) {
    const { impactOccurred, notificationOccurred } = useHapticFeedback();
    const { user } = useStore();
    const { t } = useTranslation('store');
    const { t: tc } = useTranslation('common');

    const [item, setItem] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);
    const [showScroll, setShowScroll] = useState(false);

    // Status Modal State
    const [statusModal, setStatusModal] = useState<{ isOpen: boolean; type: 'success' | 'error' | 'loading'; title: string; message?: string; onAction?: () => void; }>({
        isOpen: false,
        type: 'success',
        title: '',
    });
    const [purchaseDone, setPurchaseDone] = useState<{ isOpen: boolean; name: string; price: number; balance: number }>({
        isOpen: false,
        name: '',
        price: 0,
        balance: 0,
    });
    /** Bumped after a successful purchase so the item page reloads itself. */
    const [itemReload, setItemReload] = useState(0);

    // Scroll Listener
    useEffect(() => {
        const handleScroll = () => {
            if (!showScroll && window.scrollY > 200) setShowScroll(true);
            else if (showScroll && window.scrollY <= 200) setShowScroll(false);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [showScroll]);

    const normalizeData = (data: any) => ({
        ...data,
        card_name: data.displayName || data.name || data.card_name,
        image_url: data.profileImage || data.image || data.image_url || data.image_filename ||
            data.avatarUrl || data.ownerImage || data.ownerAvatar || data.seller_avatar || data.owner_avatar,
        background_color: data.waveColor || data.background_color || data.base_color,
        rarity_level: data.rarity || data.rarity_level,
        rank_level: data.rank || data.rank_level,
        owner_username: data.username || data.seller?.username || data.owner_username,
        owner_id: data.seller?.id || data.owner_id,
        owner_name: data.owner?.name || data.seller?.name ||
            data.seller_first_name || data.seller_name || data.seller_display_name || data.owner_name || data.username,
        seller_telegram_id: data.seller_telegram_id || data.owner_telegram_id || null,
        current_owner_id: data.current_owner_id ?? data.owner_id ?? data.seller_id ?? null,
        owner_avatar: data.seller?.avatar || data.owner_avatar || data.owner?.avatar ||
            (data.seller?.photo_url) || (data.seller?.photo),
        listing_price: data.price || data.listing_price || data.base_price || 0,
        minted_at: data.minted_at || new Date().toISOString(),
        serial_number: data.serial || data.serial_number,
        listing_id: data.listing_id || null,
        id: data.id || data.card_id || data.definition_id,
        // Ensure properties for Card component
        displayName: data.displayName || data.name || data.card_name,
        profileImage: data.profileImage || data.image || data.image_url || data.image_filename ||
            data.avatarUrl || data.ownerImage || data.ownerAvatar || data.seller_avatar || data.owner_avatar,
        waveColor: data.waveColor || data.background_color || data.base_color,
        price: data.price || data.listing_price || data.base_price || 0
    });

    // Data Loading & Normalization
    useEffect(() => {
        if (id && typeof id === 'object') {
            setItem(normalizeData(id));
            setLoading(false);
            return;
        }
        if (!id) {
            setLoading(false);
            return;
        }

        const fetchItemDetails = async () => {
            try {
                const isUmkt = String(id).startsWith('umkt_');
                const umktId = isUmkt ? String(id).replace(/^umkt_/, '') : id;

                if (isUmkt) {
                    try {
                        const umktRes = await usernameMarketApi.auctionDetail(Number(umktId));
                        const umktData = await umktRes.json();
                        if (umktRes.ok && umktData.success && (umktData.auction || umktData.listing)) {
                            const a = umktData.auction || umktData.listing;
                            setItem(normalizeData({
                                id: a.listing_id || umktId,
                                displayName: a.username ? `@${a.username.replace(/^@/, '')}` : `UMKT_${umktId}`,
                                username: a.username,
                                price: a.ask_price || a.price || a.current_bid || 0,
                                seller_avatar: a.photo_url || a.seller_avatar || '/icons/default-avatar.svg',
                                seller_name: a.seller_name || a.seller_first_name || a.username,
                                listing_id: a.listing_id,
                                asset_type: a.asset_type || 'ownership'
                            }));
                            setLoading(false);
                            return;
                        }
                    } catch (e) { console.error('umkt fetch error in BrandItemDetails', e); }
                }

                const res = await apiFetch(`/api/cards/${id}`);
                if (res.ok) {
                    const data = await res.json();
                    setItem(normalizeData(data));
                } else {
                    setItem(normalizeData({
                        id: id,
                        displayName: String(id).startsWith('umkt_') ? `@${String(id).replace(/^(umkt_|umkt_asset_)/, '')}` : `ITEM_${id}`,
                        username: String(id),
                        price: 0,
                        seller_avatar: '/icons/default-avatar.svg',
                        seller_name: 'Market',
                        asset_type: 'ownership'
                    }));
                }
            } catch (e) {
                console.error("Failed to fetch item", e);
                setItem(normalizeData({
                    id: id,
                    displayName: String(id).startsWith('umkt_') ? `@${String(id).replace(/^(umkt_|umkt_asset_)/, '')}` : `ITEM_${id}`,
                    username: String(id),
                    price: 0,
                    seller_avatar: '/icons/default-avatar.svg',
                    seller_name: 'Market',
                    asset_type: 'ownership'
                }));
            } finally {
                setLoading(false);
            }
        };

        fetchItemDetails();
    }, [id, itemReload]);



    const handleBuy = async () => {
        if (!item || !user) return;

        impactOccurred('heavy');
        setIsProcessing(true);

        const isUmkt = String(item.id || '').startsWith('umkt_') || item.source === 'umkt_store';
        if (isUmkt) {
            try {
                const res = await usernameMarketApi.buy(Number(item.listingId ?? String(item.id).replace('umkt_', '')));
                const data = await res.json();
                if (data.success || res.ok) {
                    notificationOccurred('success');
                    setPurchaseDone({
                        isOpen: true,
                        name: item.card_name || item.username || '',
                        price: Number(item.ask_price || item.price || 0),
                        balance: Number(data.new_balance ?? data.newBalance ?? 0),
                    });
                } else {
                    notificationOccurred('error');
                    setStatusModal({
                        isOpen: true,
                        type: 'error',
                        title: t('purchaseFailed'),
                        message: data.error || t('unknownErrorOccurred')
                    });
                }
            } catch (e) {
                console.error("Purchase error", e);
                setStatusModal({
                    isOpen: true,
                    type: 'error',
                    title: t('errorTitle'),
                    message: t('connectionFailedRetry')
                });
            } finally {
                setIsProcessing(false);
            }
            return;
        }

        const isStoreItem = item.source === 'store' || item.type === 'STORE' || !item.listing_id;
        const endpoint = isStoreItem ? '/api/cards/buy' : '/api/items/buy';

        const payload = isStoreItem
            ? { userId: user.telegram_id, cardId: item.id }
            : { userId: user.telegram_id, listingId: item.listing_id || item.id };

        try {
            const res = await apiFetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await res.json();
            if (data.success || res.ok) {
                notificationOccurred('success');
                setPurchaseDone({
                    isOpen: true,
                    name: item.card_name || item.username || '',
                    price: displayPrice,
                    balance: Number(data.new_balance ?? data.newBalance ?? 0),
                });
            } else {
                notificationOccurred('error');
                setStatusModal({
                    isOpen: true,
                    type: 'error',
                    title: t('purchaseFailed'),
                    message: data.error || t('unknownErrorOccurred')
                });
            }
        } catch (e) {
            console.error("Purchase error", e);
            setStatusModal({
                isOpen: true,
                type: 'error',
                title: t('errorTitle'),
                message: t('connectionFailedRetry')
            });
        } finally {
            setIsProcessing(false);
        }
    };

    const handleShare = () => {
        impactOccurred('light');
        // Use brand_ prefix for brand items
        const link = `${import.meta.env.VITE_TELEGRAM_BOT_APP_URL}?startapp=brand_${item.id}`;
        const text = t('shareCardOnKite', { name: item.card_name });
        const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(text)}`;

        // @ts-ignore
        if (window.Telegram?.WebApp?.openTelegramLink) window.Telegram.WebApp.openTelegramLink(shareUrl);
        else window.open(shareUrl, '_blank');
    };

    // --- RENDER LOGIC ---

    if (loading) {
        return <div className="min-h-screen bg-tg-bg flex items-center justify-center text-white">{t('loadingAsset')}</div>;
    }

    if (!item) {
        return <div className="min-h-screen bg-tg-bg flex items-center justify-center text-white">{t('assetNotFound')}</div>;
    }

    const displayPrice = Number(item.listing_price || item.price || 0);
    const isPaid = displayPrice > 0;

    // Map to ASSET_DATA for consistent rendering with CardDetailsPage
    const ASSET_DATA = {
        username: item.card_name,
        img: item.image_url,
        color: item.background_color || '#0088CC',
        price: isPaid ? `${displayPrice}` : t('free'),
        serial: `#${item.serial_number || 'GEN-1'}`,
        stats: {
            views: item.views_count || '0',
            likes: item.likes_count || '0',
            shares: item.share_count || '0'
        },
        owner: {
            uid: `USER-${item.owner_id || 'SYS'}`,
            id: item.seller_telegram_id ? `ID: ${item.seller_telegram_id}` : (item.current_owner_id ? `ID: ${item.current_owner_id}` : 'Hidden'),
            name: item.owner_name || item.owner_username || 'System',
            avatar: item.owner_avatar || 'https://i.pravatar.cc/150', // Replace with safe default
            rank: item.rank_level || 'Public Member'
        }
    };

    const statusText = isPaid ? t('statusActiveForSale') : t('statusNotListed');
    const statusColor = isPaid ? 'bg-green-500 text-green-500' : 'bg-gray-500 text-gray-500';

    const isOwner = item.owner_id === user?.id;

    return (
        <div 
            className="min-h-screen bg-tg-bg text-white selection:bg-blue-500/30 overflow-x-hidden font-sans antialiased pb-24 relative"
            style={{ '--wave-color': item.background_color || '#0088CC' } as any}
        >
            <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;600;800&family=Space+Grotesk:wght@500;700;900&display=swap" rel="stylesheet" />

            {/* Navigation Header */}
            <nav className="h-20 pt-6 px-6 flex items-center justify-between sticky top-0 z-[100] bg-tg-bg/60 backdrop-blur-2xl border-b border-white/[0.03]">
                <button
                    onClick={() => {
                        impactOccurred('light');
                        onBack();
                    }}
                    className="w-10 h-10 rounded-xl bg-white/[0.03] border border-white/[0.05] flex items-center justify-center hover:bg-white/[0.05] transition-colors"
                >
                    <ChevronLeft size={18} className="text-gray-400" />
                </button>

                <div className="text-center">
                    <div className="flex items-center gap-1.5 justify-center mb-0.5">
                        <div className={`w-1 h-1 rounded-full animate-ping ${statusColor.split(' ')[0]}`} />
                        <span className={`text-[7px] font-black uppercase tracking-[0.4em] ${statusColor.split(' ')[1]}/80`}>{t('marketStatus')}</span>
                    </div>
                    <h2 className="text-[11px] font-black uppercase tracking-widest text-white/90" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{statusText}</h2>
                </div>

                <button
                    onClick={handleShare}
                    className="w-10 h-10 rounded-xl bg-white/[0.03] border border-white/[0.05] flex items-center justify-center hover:bg-white/[0.05] transition-colors"
                >
                    <Share2 size={18} className="text-gray-400" />
                </button>
            </nav>

            {/* Dynamic Background Glow */}
            <div
                className="fixed top-[-20%] left-[-10%] w-[120%] h-[80%] blur-[150px] rounded-full z-0 pointer-events-none"
                style={{
                    background: `radial-gradient(circle at center, ${item.background_color || '#0088CC'} 0%, transparent 70%)`,
                    opacity: 0.14
                }}
            />

            <main className="max-w-md mx-auto px-4 pt-6 relative z-10 space-y-6">

                {/* HERO: Username */}
                <div className="flex flex-col items-center justify-center py-6">
                    <h1 className="text-3xl font-black tracking-tighter uppercase leading-none text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.5)]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                        {ASSET_DATA.username}
                    </h1>
                    <div className="flex items-center justify-center gap-2 text-white/40 mt-3">
                        <span className="text-[10px] font-bold uppercase tracking-widest">{t('editionLabel', { value: item.rarity_level || t('commonRank') })}</span>
                        <div className="w-1 h-1 bg-white/20 rounded-full" />
                        <span className="text-[10px] font-bold uppercase tracking-widest">{t('rankLabel', { value: item.rank_level || t('standard') })}</span>
                    </div>
                </div>

                {/* Price Info - Now full width and centered */}
                <div className="p-5 rounded-[28px] glass-strong border border-white/10 shadow-[0_8px_30px_rgba(0,0,0,0.35)] flex items-center justify-between group">
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-2xl flex items-center justify-center bg-blue-500/20 border border-blue-500/20 shadow-lg shadow-blue-500/10">
                            <ShoppingBag size={20} className="text-blue-400 group-hover:scale-110 transition-transform" />
                        </div>
                        <div className="flex flex-col">
                            <span className="text-[10px] font-black text-blue-400 uppercase tracking-widest leading-none mb-1">{t('marketPrice')}</span>
                            <div className="flex items-center gap-2">
                                <GramIcon className="w-6 h-6" />
                                <span className="text-2xl font-black tracking-tight text-white italic" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{ASSET_DATA.price}</span>
                                <span className="text-[10px] font-black text-white/40 uppercase ml-1">GRAM</span>
                            </div>
                        </div>
                    </div>
                    <div className="w-1 h-8 rounded-full bg-white/5" />
                    <div className="flex flex-col items-end">
                        <span className="text-[8px] font-black text-white/20 uppercase tracking-tighter">{t('status')}</span>
                        <span className="text-[10px] font-black text-green-400 uppercase tracking-widest">{tc('active')}</span>
                    </div>
                </div>

                {/* Asset Owner Section (mirrors auction page) */}
                <div className="group relative bg-gradient-to-r from-white/[0.03] to-transparent border border-white/[0.05] rounded-[24px] p-4 flex items-center justify-between overflow-hidden">
                    <div className="flex items-center gap-4 relative z-10">
                        <div className="relative">
                            <img src={item.owner_avatar || 'https://i.pravatar.cc/150'} className="w-11 h-11 rounded-xl object-cover ring-1 ring-white/10" alt={t('owner')} />
                        </div>
                        <div className="flex flex-col">
                            <span className="text-[7px] font-black text-blue-500/60 uppercase tracking-widest">{t('assetOwner')}</span>
                            <span className="text-[13px] font-bold text-white/90 group-hover:text-blue-400">{ASSET_DATA.owner.name}</span>
                            <span className="text-[9px] font-mono text-gray-500">{ASSET_DATA.owner.id}</span>
                        </div>
                    </div>
                    <button
                        onClick={() => {
                            if (item.seller_telegram_id && onNavigate) {
                                impactOccurred('medium');
                                onNavigate('public_profile', {
                                    telegram_id: item.seller_telegram_id,
                                    username: item.seller_username || item.owner_username || item.owner_name
                                });
                            }
                        }}
                        disabled={!item.seller_telegram_id || !onNavigate}
                        className="flex items-center gap-2 px-3 py-1.5 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/20 rounded-xl transition-all active:scale-95 group disabled:opacity-30 relative z-10"
                    >
                        <User size={12} className="text-blue-400 group-hover:scale-110 transition-transform" />
                        <span className="text-[9px] font-black text-blue-400 uppercase tracking-widest">{t('viewProfile')}</span>
                    </button>
                </div>

                {/* Price Valuation & Intelligence Engine (MySQL cached) */}
                {(ASSET_DATA.username || item?.username || item?.card_name) && (
                    <UsernameValuationCard username={ASSET_DATA.username || item?.username || item?.card_name} />
                )}

                {/* Smart Contract */}
                <div className="bg-[#070b14]/50 border border-white/[0.05] rounded-[28px] p-5 space-y-4 relative overflow-hidden">
                    <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
                    <div className="flex items-center justify-between group/item relative z-10">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-xl bg-white/[0.03] border border-white/[0.05] flex items-center justify-center text-gray-400 group-hover/item:text-blue-400 transition-colors">
                                <FileCode size={18} />
                            </div>
                            <div className="flex flex-col">
                                <span className="text-[8px] font-black text-gray-500 uppercase tracking-widest">{t('smartContract')}</span>
                                <span className="text-[11px] font-black text-white/90 tracking-wider">{ASSET_DATA.username} · {ASSET_DATA.price} GRAM</span>
                            </div>
                        </div>
                        <div className="px-2 py-1 bg-green-500/10 rounded-md border border-green-500/20 text-[7px] font-black text-green-400 uppercase tracking-widest">{t('verified')}</div>
                    </div>
                    <div className="h-px bg-gradient-to-r from-transparent via-white/[0.05] to-transparent relative z-10" />
                    <div className="grid grid-cols-2 gap-4 relative z-10">
                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <span className="text-[8px] font-black text-gray-500 uppercase tracking-widest">{t('minBidStep')}</span>
                            </div>
                            <div className="flex items-center gap-1.5 px-1">
                                <span className="text-sm font-black text-white/60 tabular-nums tracking-tight">—</span>
                            </div>
                        </div>
                        <div className="space-y-2 border-l border-white/[0.05] pl-4">
                            <div className="flex items-center gap-2">
                                <span className="text-[8px] font-black text-gray-500 uppercase tracking-widest">{t('commission')}</span>
                            </div>
                            <div className="flex items-center gap-1.5 px-1">
                                {isPaid && displayPrice >= 0.2 ? (
                                    <>
                                        <GramIcon className="w-3 h-3 opacity-60" />
                                        <span className="text-sm font-black text-white/60 tabular-nums tracking-tight">{(displayPrice * 0.05).toFixed(2)} (5%)</span>
                                    </>
                                ) : (
                                    <span className="text-sm font-black text-white/30 tabular-nums tracking-tight">—</span>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="h-px bg-gradient-to-r from-transparent via-white/[0.05] to-transparent relative z-10" />
                    <div className="flex items-center justify-between relative z-10">
                        <span className="text-[8px] font-black text-gray-500 uppercase tracking-widest">{t('contract')}</span>
                        <span className="text-[11px] font-black text-white/60 tracking-wider">GRAM-ERC721-V3</span>
                    </div>
                </div>

                </main>

            {/* Sticky Bottom Actions */}
            <div className="fixed bottom-0 inset-x-0 p-5 bg-tg-bg/80 backdrop-blur-lg border-t border-white/5 z-[80] max-w-md mx-auto">
                {isOwner && item.owner_username !== 'System' ? (
                    <div className="flex gap-2">
                        <button
                            disabled
                            className="flex-[2] py-4 bg-white/[0.04] backdrop-blur-xl text-white/50 font-bold rounded-2xl flex items-center justify-center gap-2 border border-white/10"
                        >
                            <span>{t('owned')}</span>
                        </button>
                        <button
                            onClick={handleShare}
                            className="flex-1 py-4 bg-white/[0.06] backdrop-blur-xl hover:bg-white/[0.10] text-white font-bold rounded-2xl flex items-center justify-center gap-2 border border-white/[0.14] transition-all active:scale-[0.98]"
                        >
                            <Share2 size={20} />
                        </button>
                    </div>
                ) : isPaid ? (
                    <button
                        onClick={handleBuy}
                        disabled={isProcessing}
                        className="w-full py-4 bg-white/[0.06] backdrop-blur-xl hover:bg-white/[0.10] text-white font-bold rounded-2xl flex items-center justify-center gap-2 border border-blue-500/25 transition-all active:scale-[0.98] disabled:opacity-50"
                    >
                        <ShoppingBag size={20} />
                        <span className="text-lg tracking-wide font-black uppercase italic" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                            {isProcessing ? t('processing') : t('purchaseDash', { price: ASSET_DATA.price })}
                        </span>
                    </button>
                ) : (
                    <div className="flex gap-2">
                        <button
                            onClick={handleBuy}
                            disabled={isProcessing}
                            className="flex-[2] py-4 bg-white/[0.06] backdrop-blur-xl hover:bg-white/[0.10] text-green-400 font-bold rounded-2xl flex items-center justify-center gap-2 border border-green-500/25 transition-all active:scale-[0.98] shadow-[0_0_15px_rgba(74,222,128,0.10)] disabled:opacity-50"
                        >
                            <ShoppingBag size={20} />
                            <span className="text-lg tracking-wide font-black uppercase italic" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                                {isProcessing ? t('processing') : t('claimForFree')}
                            </span>
                        </button>
                        <button
                            onClick={handleShare}
                            className="flex-1 py-4 bg-white/[0.06] backdrop-blur-xl hover:bg-white/[0.10] text-white font-bold rounded-2xl flex items-center justify-center gap-2 border border-white/[0.14] transition-all active:scale-[0.98]"
                        >
                            <Share2 size={20} />
                        </button>
                    </div>
                )}
            </div>

            <StatusModal
                isOpen={statusModal.isOpen}
                onClose={() => setStatusModal((prev) => ({ ...prev, isOpen: false }))}
                type={statusModal.type}
                title={statusModal.title}
                message={statusModal.message}
                actionLabel={tc('close')}
                onAction={statusModal.onAction}
                glass
            />

            <PurchaseSuccessModal
                isOpen={purchaseDone.isOpen}
                onClose={() => setPurchaseDone((prev) => ({ ...prev, isOpen: false }))}
                itemName={purchaseDone.name}
                price={purchaseDone.price}
                balance={purchaseDone.balance}
                goToPage="card_view"
                goToParam={id}
                onNavigate={onNavigate}
            />
        </div>
    );
}


